export interface PastureVertexDraft {
  readonly key: string;
  mode: 'existing' | 'new';
  poleId: string;
  tagId: string;
  bindingId: string;
  latitude: number | null;
  longitude: number | null;
}

export interface PastureReference {
  readonly id: string;
  readonly status: string;
  readonly poleIds: readonly string[];
}

export function vertexDraft(id: () => string): PastureVertexDraft {
  return {
    key: id(),
    mode: 'new',
    poleId: id(),
    tagId: '',
    bindingId: id(),
    latitude: null,
    longitude: null,
  };
}

export function existingVertexDraft(id: () => string, poleId: string): PastureVertexDraft {
  return {
    ...vertexDraft(id),
    mode: 'existing',
    poleId,
  };
}

function coordinateOf(vertex: PastureVertexDraft): { latitude: number; longitude: number } {
  if (
    vertex.latitude === null ||
    vertex.longitude === null ||
    !Number.isFinite(vertex.latitude) ||
    !Number.isFinite(vertex.longitude) ||
    vertex.latitude < -90 ||
    vertex.latitude > 90 ||
    vertex.longitude < -180 ||
    vertex.longitude > 180
  ) {
    throw new Error('Informe latitude entre -90 e 90 e longitude entre -180 e 180 para cada poste novo.');
  }
  return { latitude: vertex.latitude, longitude: vertex.longitude };
}

export function perimeterFromDrafts(vertices: readonly PastureVertexDraft[]): Record<string, unknown>[] {
  if (vertices.length < 3) throw new Error('O pasto precisa ter pelo menos três postes.');

  const poleIds = new Set<string>();
  const tagIds = new Set<string>();
  return vertices.map((vertex, ordinal) => {
    if (vertex.poleId === '') throw new Error(`Selecione o poste do vértice ${ordinal + 1}.`);
    if (poleIds.has(vertex.poleId)) throw new Error('O mesmo poste não pode aparecer duas vezes no perímetro.');
    poleIds.add(vertex.poleId);

    if (vertex.mode === 'existing') return { ordinal, poleId: vertex.poleId };

    if (vertex.tagId === '') throw new Error(`Selecione a etiqueta do poste novo ${ordinal + 1}.`);
    if (tagIds.has(vertex.tagId)) throw new Error('A mesma etiqueta não pode ser usada em dois postes novos.');
    tagIds.add(vertex.tagId);
    const coordinate = coordinateOf(vertex);

    return {
      ordinal,
      poleId: vertex.poleId,
      newPole: {
        coordinate: { ...coordinate, origin: 'MANUAL_MAP', accuracyMeters: null },
        tagId: vertex.tagId,
        bindingId: vertex.bindingId,
        confirmation: { method: 'ADMIN_SELECTION', confirmedCode: null },
      },
    };
  });
}

export function affectedPastureIds(
  pastures: readonly PastureReference[],
  poleId: string,
): string[] {
  return pastures
    .filter((pasture) => pasture.status === 'active' && pasture.poleIds.includes(poleId))
    .map((pasture) => pasture.id);
}
